import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Badge } from "@/components/ui/badge";

export default function SentimentAnalysis({ activities = [] }) {
  const [analysis, setAnalysis] = useState(null);

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const activitiesText = activities
        .map(a => `${a.type}: ${a.title} - ${a.description || ""}`)
        .join("\n");

      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `Analyser følgende kommunikasjonslogger og aktivitetsnotater for en investor. Gi en sentimentanalyse og anbefaling for neste steg:\n\n${activitiesText}\n\nGi et JSON-svar med sentiment (positive/neutral/negative), score (0-100), key_insights (array av innsikter), og recommended_actions (array av anbefalte handlinger).`,
        response_json_schema: {
          type: "object",
          properties: {
            sentiment: { type: "string", enum: ["positive", "neutral", "negative"] },
            score: { type: "number" },
            key_insights: { type: "array", items: { type: "string" } },
            recommended_actions: { type: "array", items: { type: "string" } },
            summary: { type: "string" }
          }
        }
      });

      return result;
    },
    onSuccess: (data) => {
      setAnalysis(data);
    },
  });

  const sentimentConfig = {
    positive: {
      icon: TrendingUp,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      label: "Positiv"
    },
    neutral: {
      icon: Minus,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      label: "Nøytral"
    },
    negative: {
      icon: TrendingDown,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      label: "Negativ"
    }
  };

  const currentSentiment = analysis?.sentiment || "neutral";
  const config = sentimentConfig[currentSentiment];
  const Icon = config.icon;

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <Brain className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">AI Sentimentanalyse</h3>
            <p className="text-sm text-slate-500">Basert på aktivitetslogger</p>
          </div>
        </div>
        <Button
          onClick={() => analyzeMutation.mutate()}
          disabled={analyzeMutation.isPending || activities.length === 0}
          size="sm"
        >
          {analyzeMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyserer...
            </>
          ) : (
            "Analyser"
          )}
        </Button>
      </div>

      {activities.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4">
          Ingen aktiviteter å analysere ennå
        </p>
      )}

      {analysis && (
        <div className="space-y-4">
          {/* Sentiment Score */}
          <div className={`p-4 rounded-xl border ${config.bgColor} ${config.borderColor}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <span className={`font-semibold ${config.color}`}>{config.label}</span>
              </div>
              <Badge className={`${config.bgColor} ${config.color} border-0`}>
                Score: {analysis.score}/100
              </Badge>
            </div>
            {analysis.summary && (
              <p className="text-sm text-slate-700 mt-2">{analysis.summary}</p>
            )}
          </div>

          {/* Key Insights */}
          {analysis.key_insights && analysis.key_insights.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">Nøkkelinnsikter</h4>
              <ul className="space-y-2">
                {analysis.key_insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-zinc-900 mt-0.5">•</span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Actions */}
          {analysis.recommended_actions && analysis.recommended_actions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">Anbefalte handlinger</h4>
              <ul className="space-y-2">
                {analysis.recommended_actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 p-2 bg-slate-50 rounded-lg">
                    <span className="text-emerald-600 mt-0.5">→</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}